import IconMinus from 'assets/icons/minus.svg';
import IconPlus from 'assets/icons/plus.svg';
import cn from 'classnames';
import Markdown from 'components/markdown';
import { m } from 'framer-motion';
import useMobile from 'hooks/common/use-mobile';
import { ContentTabs } from 'modules/product-details/components/content';
import { StaticPDPProduct } from 'modules/product-details/types';
import { useEffect, useRef } from 'react';
import { Gtm, gtm } from 'tracking/gtm';
import { slugify } from 'utils/strings';
import useVisibilityTracking from './use-visibility-tracking';

interface TabProps {
  activeTab: number | null;
  contentClassName?: string;
  index: number;
  product: StaticPDPProduct;
  tab: ContentTabs[number];
  onContentTabChange: (newIndex: number | null) => void;
}

/**
 * Tab component that renders the content for each tab.
 */
const Tab: React.FC<TabProps> = ({
  activeTab,
  contentClassName,
  index,
  product,
  tab,
  onContentTabChange,
}) => {
  const isMobile = useMobile();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const trackingRef = useRef<boolean>(false);

  const { content, title } = tab;

  useVisibilityTracking({
    activeTab,
    contentRef,
    index,
    tabTitle: title,
    trackingRef,
  });

  // Scroll to the button when the tab is active on mobile
  useEffect(() => {
    const scrollToButton = (): void => {
      const headerHeight = document.getElementById('header')?.clientHeight ?? 0;
      const buttonPosition =
        (buttonRef.current?.getBoundingClientRect().top ?? 0) +
        window.scrollY -
        headerHeight;

      window.scrollTo({
        top: buttonPosition,
        behavior: 'smooth',
      });
    };

    if (isMobile && activeTab === index && buttonRef.current) {
      // Delay the scroll action to ensure that the previous tab has finished collapsing
      const timeoutId = setTimeout(scrollToButton, 350);

      return () => clearTimeout(timeoutId);
    }
  }, [activeTab, index, isMobile]);

  const handleTabClick = (): void => {
    gtm({
      group: Gtm.GroupName.Product,
      name: Gtm.ProductEventName.InfoTabClick,
      data: {
        product: {
          productTitle: product.title,
          productId: product.id + '',
          productType: product.productType,
          tags: product.tags.join(','),
          tabName: title,
        },
      },
    });
    onContentTabChange(activeTab === index ? null : index);
  };

  return (
    <>
      <button
        aria-controls={`${slugify(title)}-tab`}
        aria-selected={activeTab === index ? 'true' : 'false'}
        className={cn(
          'flex w-full items-center justify-between border-b border-grey-100 py-3 text-left font-medium text-grey-900 focus:outline-hidden lg:hidden'
        )}
        data-test={`producttab-${slugify(title)}`}
        id={slugify(title)}
        onClick={handleTabClick}
        role="tab"
        ref={buttonRef}
      >
        <div dangerouslySetInnerHTML={{ __html: title }} />
        <m.span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-grey-100 text-text-primary"
          animate={{
            rotate: activeTab === index ? 0 : 180,
          }}
          transition={{ duration: 0.35 }}
        >
          {activeTab === index ? (
            <IconMinus className="w-3" />
          ) : (
            <IconPlus className="w-3" />
          )}
        </m.span>
      </button>
      <m.div
        aria-labelledby={slugify(title)}
        className={cn(
          contentClassName,
          'grid border-b border-grey-100 focus:outline-hidden lg:border-0'
        )}
        initial={{ opacity: 0, height: 0 }}
        animate={
          activeTab === index
            ? { opacity: 1, height: 'auto' }
            : { opacity: 0, height: 0 }
        }
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
        data-test={slugify(title)}
        id={`${slugify(title)}-tab`}
        role="tabpanel"
        tabIndex={index}
      >
        <div className="overflow-hidden" ref={contentRef}>
          <div className="container py-6 md:py-8 lg:py-10">
            <Markdown className="w-full">{content}</Markdown>
          </div>
        </div>
      </m.div>
    </>
  );
};

export default Tab;
